import { z } from '../../../zod';
import { MESSAGE_VARIABLES_HELP, MESSAGE_VARIABLES_OUTPUT_HELP } from '../messageVariables';
import { ObjectIdStringValidationSchema } from '../../../shared/ObjectId';

export const RedirectUrlPathSegmentValidationSchema = z.object({
  key: z.string().nullish(),
  identifier: z.string().nullish(),
});

export const RedirectUrlQueryParameterValidationSchema = z.object({
  parameter: z.string().nullish(),
  key: z.string().nullish(),
  identifier: z.string().nullish(),
});

const RedirectSettingsInputValidationSchema = z
  .object({
    enabled: z.boolean().nullish(),
    url: z.string().nullish(),
  })
  .nullish()
  .meta({
    description: 'If enabled, redirects the customer to the specified URL before the page loads.',
    example: {
      enabled: true,
      url: 'https://example.com/redirected-url',
    },
  });

export const CreateRedirectUrlPathSegmentValidationSchema = z
  .object({
    key: z.string().nullish(),
    identifier: z.string().nullish(),
    fieldKey: z.string().nullish(),
  })
  .superRefine((value, ctx) => {
    if (value.key === 'fields') {
      if (!value.fieldKey) {
        ctx.addIssue({
          code: 'custom',
          path: ['fieldKey'],
          message: 'fieldKey is required when key is "fields".',
        });
      }
      if (value.identifier != null) {
        ctx.addIssue({
          code: 'custom',
          path: ['identifier'],
          message: 'identifier is not allowed when key is "fields". Use fieldKey instead.',
        });
      }
      return;
    }

    if (!value.identifier) {
      ctx.addIssue({
        code: 'custom',
        path: ['identifier'],
        message: 'identifier is required unless key is "fields".',
      });
    }
    if (value.fieldKey != null) {
      ctx.addIssue({
        code: 'custom',
        path: ['fieldKey'],
        message: 'fieldKey is only allowed when key is "fields".',
      });
    }
  });

export const CreateRedirectUrlQueryParameterValidationSchema = z
  .object({
    parameter: z.string().nullish(),
    key: z.string().nullish(),
    identifier: z.string().nullish(),
    fieldKey: z.string().nullish(),
  })
  .superRefine((value, ctx) => {
    if (value.key === 'fields') {
      if (!value.fieldKey) {
        ctx.addIssue({
          code: 'custom',
          path: ['fieldKey'],
          message: 'fieldKey is required when key is "fields".',
        });
      }
      if (value.identifier != null) {
        ctx.addIssue({
          code: 'custom',
          path: ['identifier'],
          message: 'identifier is not allowed when key is "fields". Use fieldKey instead.',
        });
      }
      return;
    }

    if (!value.identifier) {
      ctx.addIssue({
        code: 'custom',
        path: ['identifier'],
        message: 'identifier is required unless key is "fields".',
      });
    }
    if (value.fieldKey != null) {
      ctx.addIssue({
        code: 'custom',
        path: ['fieldKey'],
        message: 'fieldKey is only allowed when key is "fields".',
      });
    }
  });

// Update-time counterpart to CreateRedirectUrlPathSegmentValidationSchema.
// Update requests can't create new fields, so field references resolve by
// ObjectId (`fieldId`) instead of the create-time `fieldKey` string — mirrors
// apps/api/src/pages/schemas/common/redirectsAndConfirmationSchemas.ts's
// UpdateRedirectUrlPathSegmentValidationSchema (packages cannot import from
// apps/api, so this is a deliberate, documented duplication).
export const UpdateRedirectUrlPathSegmentValidationSchema = z
  .object({
    key: z.string().nullish().meta({
      description:
        'What this path segment resolves to. Set to `fields` to reference a field value collected on the page (pair with `fieldId`); any other value is a static token name (pair with `identifier`).',
      example: 'fields',
    }),
    identifier: z.string().nullish().meta({
      description:
        'Static value or token identifier for this path segment. Required unless `key` is `fields` — in that case use `fieldId` instead and omit this. Rejected when `key` is `fields`.',
      example: 'orderId',
    }),
    fieldId: ObjectIdStringValidationSchema.nullish().meta({
      description:
        "The persisted field's ID, as `fieldId`. Required when `key` is `fields`; on update, field references resolve by `fieldId` rather than the create-time `fieldKey` string, since update requests can't create new fields to key against. Get current field IDs from the page detail response. Rejected unless `key` is `fields`.",
      example: '507f1f77bcf86cd799439011',
    }),
  })
  .meta({
    description:
      "A single path segment appended to the redirect URL on update. To reference a field value, set `key` to `fields` and `fieldId` to the field's persisted ID (from the page detail response). For any other token, set `key` to the token name and `identifier` to its value.",
    example: { key: 'fields', fieldId: '507f1f77bcf86cd799439011' },
  })
  .superRefine((value, ctx) => {
    if (value.key === 'fields') {
      if (!value.fieldId) {
        ctx.addIssue({
          code: 'custom',
          path: ['fieldId'],
          message: 'fieldId is required when key is "fields".',
        });
      }
      if (value.identifier != null) {
        ctx.addIssue({
          code: 'custom',
          path: ['identifier'],
          message: 'identifier is not allowed when key is "fields". Use fieldId instead.',
        });
      }
      return;
    }

    if (!value.identifier) {
      ctx.addIssue({
        code: 'custom',
        path: ['identifier'],
        message: 'identifier is required unless key is "fields".',
      });
    }
    if (value.fieldId != null) {
      ctx.addIssue({
        code: 'custom',
        path: ['fieldId'],
        message: 'fieldId is only allowed when key is "fields".',
      });
    }
  });

// Update-time counterpart to CreateRedirectUrlQueryParameterValidationSchema.
// See UpdateRedirectUrlPathSegmentValidationSchema above for why `fieldId`
// replaces `fieldKey` on update.
export const UpdateRedirectUrlQueryParameterValidationSchema = z
  .object({
    parameter: z.string().nullish().meta({
      description: 'The query string parameter name to set on the redirect URL.',
      example: 'email',
    }),
    key: z.string().nullish().meta({
      description:
        'What this query value resolves to. Set to `fields` to reference a field value collected on the page (pair with `fieldId`); any other value is a static token name (pair with `identifier`).',
      example: 'fields',
    }),
    identifier: z.string().nullish().meta({
      description:
        'Static value or token identifier for this query parameter. Required unless `key` is `fields` — in that case use `fieldId` instead and omit this. Rejected when `key` is `fields`.',
      example: 'orderId',
    }),
    fieldId: ObjectIdStringValidationSchema.nullish().meta({
      description:
        "The persisted field's ID, as `fieldId`. Required when `key` is `fields`; on update, field references resolve by `fieldId` rather than the create-time `fieldKey` string, since update requests can't create new fields to key against. Get current field IDs from the page detail response. Rejected unless `key` is `fields`.",
      example: '507f1f77bcf86cd799439011',
    }),
  })
  .meta({
    description:
      "A single query parameter appended to the redirect URL on update. To reference a field value, set `key` to `fields` and `fieldId` to the field's persisted ID (from the page detail response). For any other token, set `key` to the token name and `identifier` to its value.",
    example: { parameter: 'email', key: 'fields', fieldId: '507f1f77bcf86cd799439011' },
  })
  .superRefine((value, ctx) => {
    if (value.key === 'fields') {
      if (!value.fieldId) {
        ctx.addIssue({
          code: 'custom',
          path: ['fieldId'],
          message: 'fieldId is required when key is "fields".',
        });
      }
      if (value.identifier != null) {
        ctx.addIssue({
          code: 'custom',
          path: ['identifier'],
          message: 'identifier is not allowed when key is "fields". Use fieldId instead.',
        });
      }
      return;
    }

    if (!value.identifier) {
      ctx.addIssue({
        code: 'custom',
        path: ['identifier'],
        message: 'identifier is required unless key is "fields".',
      });
    }
    if (value.fieldId != null) {
      ctx.addIssue({
        code: 'custom',
        path: ['fieldId'],
        message: 'fieldId is only allowed when key is "fields".',
      });
    }
  });

export const RedirectAndConfirmationFieldsInputValidationSchema = z.object({
  redirect: RedirectSettingsInputValidationSchema,
  afterPaymentAction: z.enum(['confirmation', 'checkout', 'redirect']).nullish().meta({
    description:
      'Behavior after checkout. `confirmation` shows the confirmation page. `checkout` redirects to `redirectPageId`. `redirect` sends the customer to `redirectUrl`. Defaults to `confirmation`.',
    example: 'confirmation',
  }),
  confirmationCheckoutTitle: z.string().nullish().meta({
    description: 'Confirmation title.',
    example: 'Purchase confirmed!',
  }),
  confirmationCheckoutMessage: z
    .string()
    .nullish()
    .meta({
      description: `Confirmation message. Accepts HTML or plain text. ${MESSAGE_VARIABLES_HELP}`,
      example: '<p>Thanks for your purchase. You will receive a confirmation email shortly.</p>',
    }),
  redirectUrl: z.string().nullish().meta({
    description: 'URL to redirect to after payment. Applies if `afterPaymentAction` is `redirect`.',
    example: 'https://example.com/thank-you',
  }),
  redirectUrlPath: z
    .array(CreateRedirectUrlPathSegmentValidationSchema)
    .nullish()
    .meta({
      description:
        'Additional path parameters appended to the `redirectUrl`. Applies if `afterPaymentAction` is `redirect` and `redirectUrl` is set. [Learn more](https://checkoutpage.com/docs/articles/add-field-and-variant-data-to-redirect-url)',
      example: [
        {
          key: 'fields',
          fieldKey: 'customer-email',
        },
        {
          key: 'orderId',
          identifier: 'orderId',
        },
      ],
    }),
  redirectUrlQuery: z
    .array(CreateRedirectUrlQueryParameterValidationSchema)
    .nullish()
    .meta({
      description:
        'Query parameters for the `redirectUrl`. Applies if `afterPaymentAction` is `redirect` and `redirectUrl` is set. [Learn more](https://checkoutpage.com/docs/articles/add-field-and-variant-data-to-redirect-url)',
      example: [
        {
          parameter: 'name',
          key: 'fields',
          fieldKey: 'customer-email',
        },
      ],
    }),
  redirectUrlInsideEmbed: z.boolean().nullish().meta({
    description:
      'If the page is embedded, redirect the customer within the embed instead of redirecting the main browser window. Defaults to `false`.',
    example: false,
  }),
  redirectPageId: ObjectIdStringValidationSchema.nullish().meta({
    description:
      'ID of the page (checkout, event or form) to redirect to after payment. Applies when `afterPaymentAction` is `checkout`.',
  }),
});

export const RedirectAndConfirmationResponseValidationSchema = z.object({
  redirect: z
    .object({
      enabled: z.boolean().nullish(),
      url: z.string().nullish(),
    })
    .nullish()
    .meta({
      description: 'If enabled, redirects the customer to the specified URL before the page loads.',
    }),
  afterPaymentAction: z.enum(['confirmation', 'checkout', 'redirect']).nullish().meta({
    description: 'Behavior after checkout.',
  }),
  redirectPageId: ObjectIdStringValidationSchema.nullish().meta({
    description: 'ID of the page to redirect to after payment when afterPaymentAction is checkout.',
  }),
  redirectUrl: z.string().nullish().meta({
    description: 'URL to redirect to after submission.',
  }),
  redirectUrlPath: z.array(RedirectUrlPathSegmentValidationSchema).nullish().meta({
    description: 'Additional path parameters appended to the redirect URL.',
  }),
  redirectUrlQuery: z.array(RedirectUrlQueryParameterValidationSchema).nullish().meta({
    description: 'Query parameters for the redirect URL.',
  }),
  redirectUrlInsideEmbed: z.boolean().nullish().meta({
    description: 'Redirect within the embed instead of the main browser window.',
  }),
  confirmationCheckoutTitle: z.string().nullish().meta({
    description: 'Confirmation page title.',
  }),
  confirmationCheckoutMessage: z
    .string()
    .nullish()
    .meta({
      description: `Confirmation page message. ${MESSAGE_VARIABLES_OUTPUT_HELP}`,
    }),
});
